import React, { Component } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classNames';
import debounce from 'lodash/debounce';
import { ajaxRequest } from '~/api';
import { treeNodeUtils, mergeUniqueItems } from '~/util';
import { initData } from '~/redux/actions/dataActions';
import {
	addLessonEntity,
	addTopicEntity,
	addQuizEntity,
	addQuestionEntity,
	addFinalQuizEntity,
} from '~/redux/actions/workspaceActions';

/**
 * Sidebar widget using Beautiful React DnD
 * Used for Lessons, Topics, Quizzes and Questions
 */
class SidebarWidget extends Component {
	/**
	 * Constructor
	 *
	 * @param {Object} props
	 */
	constructor( props ) {
		super( props );

		/**
		 * - checkedItems: array of selected entities, used to add multiple entities at once
		 * - searchString: our search term
		 * - mode: can be search, latest or all. Latest is the default value
		 * - list: our raw data for view all.
		 * - search_list: search results
		 * - search_list_pagination: search results, paged
		 * - latest_list: recent results
		 * - all_list: all results
		 * - all_list_pagination: all results, paged
		 */
		this.state = {
			checkedItems: [],
			searchString: '',
			mode: 'latest',
			list: this.props.data[ this.props.content ],
			search_list: [],
			search_pagination: [],
			latest_list: [],
			all_list: [],
			all_list_pagination: [],
		};

		this.filterListDebounced = debounce( this.filterList, 500 );
		this.filterList = this.filterList.bind( this );
		this.handleInputChange = this.handleInputChange.bind( this );
		this.getRecentlyAddedTopicCount = this.getRecentlyAddedTopicCount.bind(
			this
		);
	}

	/**
	 * Once the component is mounted, fetch initial data, which is the latest 5 modified entities.
	 */
	componentDidMount() {
		// Get last 5 entities
		this.latestQuery();
	}

	/**
	 * Listen to any changes to the updated raw data, for example when adding a new entity from the builder
	 *
	 * @param {*} prevProps
	 */
	componentDidUpdate( prevProps ) {
		if ( prevProps !== this.props ) {
			this.setState( {
				list: this.props.data[ prevProps.content ],
			} );
		}
	}

	/**
	 * Watch for checkbox toggling
	 *
	 * @param {string} content
	 * @param {toggleCheckboxChange} e
	 */
	toggleCheckboxChange( content, e ) {
		const { target } = e;
		const { name, checked } = target;

		// If the checkbox is checked, add to the checkedItems
		this.setState( ( prevState ) => ( {
			[ name ]: checked
				? [ ...prevState[ name ], content ]
				: prevState[ name ].filter( ( item ) => item !== content ),
		} ) );
	}

	/**
	 * Handle input changes
	 *
	 * @param {handleInputChange} e
	 */
	handleInputChange( e ) {
		const searchTerm = e.target.value.toLowerCase();

		if ( 0 !== searchTerm.length ) {
			// If we have a search term, we switch to the search mode.
			this.setState( {
				searchString: searchTerm,
				mode: 'search',
			} );
			// Filter the results containing the search term
			this.filterListDebounced( searchTerm );
		} else {
			// If we don't have a search term, we switch to the latest mode.
			this.setState( {
				searchString: '',
				mode: 'latest',
			} );

			this.props.initData( {
				field: this.props.content,
				data: this.state.all_list,
			} );
		}
	}

	/**
	 * Return the nonce from the learndash_builder_nonce element
	 *
	 * @return {string} Builder nonce
	 */
	getBuilderNonce() {
		const builder_nonce_element = document.getElementById(
			'learndash_builder_nonce'
		);
		let builder_nonce;
		if ( builder_nonce_element ) {
			builder_nonce = builder_nonce_element.value;
		}
		return builder_nonce;
	}

	/**
	 * Search within entities
	 *
	 * @param {string} searchTerm
	 */
	filterList( searchTerm ) {
		this.searchQuery( searchTerm );
	}

	/**
	 * Search through entities
	 *
	 * @param {string} searchString
	 * @param {number} page
	 */
	async searchQuery( searchString = '', page = 1 ) {
		const builder_data =
			learndash_builder_assets[
				LearnDashData.post_data.builder_post_type
			].post_data;
		builder_data.builder_nonce = this.getBuilderNonce();

		const payload = {
			action: 'learndash_builder_selector_search',
			builder_data,
			builder_query_args: {
				post_type: LearnDashData.sfwdMap[ this.props.type ],
				format: 'json',
				s: searchString,
				posts_per_page: LearnDashData.posts_per_page,
				orderby: 'title',
				order: 'ASC',
				paged: page,
			},
		};

		// Make AJAX request.
		const res = await ajaxRequest( payload );

		if ( 200 === res.status ) {
			let nodes = res.data.selector_rows;

			// Check if we are loading more data.
			if ( 1 !== page ) {
				// We must filter out existing nodes, since the search results can return existing nodes.
				nodes = mergeUniqueItems(
					this.props.data[ this.props.content ],
					res.data.selector_rows
				);
			}

			// Update the list now.
			this.props.initData( {
				field: this.props.content,
				data: nodes,
			} );

			this.setState( {
				search_list: nodes,
				search_pagination: res.data.selector_pager,
			} );
		}
	}

	/**
	 * Fetch initial data for various entities
	 *
	 * @param {number} page
	 */
	async allQuery( page = 1 ) {
		const builder_data =
			learndash_builder_assets[
				LearnDashData.post_data.builder_post_type
			].post_data;
		builder_data.builder_nonce = this.getBuilderNonce();

		const entitiesInBuilder = this.getEntitiesInBuilder();

		const payload = {
			action: 'learndash_builder_selector_pager',
			builder_data,
			builder_query_args: {
				post_type: LearnDashData.sfwdMap[ this.props.type ],
				orderby: 'title',
				order: 'ASC',
				posts_per_page: LearnDashData.posts_per_page,
				paged: page,
				format: 'json',
				post__not_in: entitiesInBuilder,
			},
		};

		// Make AJAX request.
		const res = await ajaxRequest( payload );

		if ( 200 === res.status ) {
			let nodes = res.data.selector_rows;

			// Check if we are loading more data.
			if ( 1 !== page ) {
				nodes = mergeUniqueItems(
					this.props.data[ this.props.content ],
					res.data.selector_rows
				);
			}

			// update the list now.
			this.props.initData( {
				field: this.props.content,
				data: nodes,
			} );

			this.setState( {
				all_list: nodes,
				all_list_pagination: res.data.selector_pager,
			} );
		}
	}

	/**
	 * Get the total number of topics added to the Builder from the Recent list
	 *
	 * @param {*} lessons
	 */
	getRecentlyAddedTopicCount( lessons ) {
		let totalTopics = 0;
		lessons.forEach( ( lesson ) => {
			totalTopics += lesson.tree.length;
		} );
		return totalTopics;
	}

	/**
	 * Fetch last 5 + [number of items already added to builder] entities
	 */
	async latestQuery() {
		const builder_data =
			learndash_builder_assets[
				LearnDashData.post_data.builder_post_type
			].post_data;
		builder_data.builder_nonce = this.getBuilderNonce();

		const entitiesInBuilder = this.getEntitiesInBuilder();

		const payload = {
			action: 'learndash_builder_selector_pager',
			builder_data,
			builder_query_args: {
				post_type: LearnDashData.sfwdMap[ this.props.type ],
				orderby: 'post_date',
				order: 'DESC',
				posts_per_page: LearnDashData.posts_per_page,
				paged: 1,
				format: 'json',
				post__not_in: entitiesInBuilder,
			},
		};

		// Make AJAX request.
		const res = await ajaxRequest( payload );
		if ( 200 === res.status ) {
			const nodes = res.data.selector_rows;

			// Update the list now.
			this.props.initData( {
				field: this.props.content,
				data: nodes,
			} );

			this.setState( {
				latest_list: nodes,
			} );
		}
	}

	/**
	 * Load more entities from the AJAX endpoint
	 */
	loadMore() {
		if ( 'search' === this.state.mode ) {
			this.searchQuery(
				this.state.searchString,
				this.state.search_pagination.next_page
			);
		} else if ( 'all' === this.state.mode ) {
			this.allQuery( this.state.all_list_pagination.next_page );
		}
	}

	/**
	 * Add entity to our workspace (builder area)
	 *
	 * @param {string} content
	 * @param {addEntity} e
	 */
	addEntity( content, e ) {
		e.preventDefault();

		const {
			type,
			addLessonEntity,
			addTopicEntity,
			addQuizEntity,
			addQuestionEntity,
			addFinalQuizEntity,
			workspace,
		} = this.props;

		if ( 'lesson' === type ) {
			addLessonEntity( content );
		} else if ( 'topic' === type ) {
			addTopicEntity( content );
		} else if ( 'quiz' === type ) {
			if (
				null === workspace.present.activeLesson &&
				'sfwd-quiz' !== LearnDashData.post_data.builder_post_type
			) {
				// If there is no active lesson, add to final quizzes
				addFinalQuizEntity( workspace.present.quizzes.length, content );
			} else {
				addQuizEntity( content );
			}
		} else if ( 'question' === type ) {
			addQuestionEntity( content );
		}
	}

	/**
	 * Check if a topic has been added to the builder
	 *
	 * @param {Object} droppedNode The Node that is being checked against
	 */
	canAddTopic( droppedNode ) {
		const { present } = this.props.workspace;

		let filtered_items = [];
		for ( const lesson of present.lessons ) {
			filtered_items = lesson.tree.filter(
				( added_item ) => added_item.ID === droppedNode.ID
			);
			if ( 0 < filtered_items.length ) {
				break;
			}
		}

		return 0 === filtered_items.length;
	}

	/**
	 * Check if a quiz has been added to the builder
	 *
	 * @param {number} entityID
	 */
	canAddQuiz( entityID ) {
		const { present } = this.props.workspace;

		const finalQuizzesNodes = Object.values( present.quizzes );
		if (
			treeNodeUtils.getNodeByKey(
				finalQuizzesNodes,
				parseInt( entityID )
			)
		)
			return false;

		let filtered_items = [];

		for ( const lesson of present.lessons ) {
			filtered_items = lesson.tree.filter(
				( item ) => 'sfwd-quiz' === item.type && entityID === item.ID
			);
			if ( 0 < filtered_items.length ) {
				break;
			}

			const topics = lesson.tree.filter(
				( added_item ) => 'sfwd-topic' === added_item.type
			);
			for ( const topic of topics ) {
				filtered_items = topic.tree.filter(
					( item ) => entityID === item.ID
				);
				if ( 0 < filtered_items.length ) {
					break;
				}
			}
			if ( 0 < filtered_items.length ) {
				break;
			}
		}

		return 0 === filtered_items.length;
	}

	/**
	 * Check if we can add an entity
	 *
	 * @param {number} entityID
	 */
	canAddEntity( entityID ) {
		// Get workspace data
		const { present } = this.props.workspace;

		let entityNodes = null;
		if ( 'sfwd-quiz' !== LearnDashData.post_data.builder_post_type ) {
			entityNodes = Object.values( present.lessons );
		} else {
			entityNodes = Object.values( present.questions );
		}

		const droppedNode = treeNodeUtils.getNodeByKey(
			entityNodes,
			parseInt( entityID )
		);

		if (
			'sfwd-quiz' !== LearnDashData.post_data.builder_post_type &&
			'lessons' === this.props.content
		)
			return null === droppedNode;

		if (
			'sfwd-quiz' !== LearnDashData.post_data.builder_post_type &&
			'quizzes' === this.props.content
		) {
			if ( ! droppedNode ) {
				return this.canAddQuiz( entityID );
			}
		}

		if (
			'sfwd-quiz' !== LearnDashData.post_data.builder_post_type &&
			'topics' === this.props.content &&
			droppedNode
		) {
			return this.canAddTopic( droppedNode );
		}

		return null === droppedNode;
	}

	/**
	 * Get IDs of entities already added to the builder
	 */
	getEntitiesInBuilder() {
		const { present } = this.props.workspace;

		let entityNodes = [];
		if ( 'sfwd-quiz' !== LearnDashData.post_data.builder_post_type ) {
			if ( 'lessons' === this.props.content ) {
				entityNodes = Object.values( present.lessons );
			} else if ( 'topics' === this.props.content ) {
				for ( const lesson of present.lessons ) {
					const topics = lesson.tree.reduce( ( acc, item ) => {
						if ( 'sfwd-topic' === item.type ) {
							acc.push( item );
						}
						return acc;
					}, [] );
					entityNodes.push( ...topics );
				}
			} else if ( 'quizzes' === this.props.content ) {
				entityNodes = [ ...present.quizzes ];
				present.lessons.forEach( ( lesson ) => {
					entityNodes = [
						...entityNodes,
						...lesson.tree.filter(
							( entity ) => 'sfwd-quiz' === entity.type
						),
					];

					lesson.tree.forEach( ( entity ) => {
						const tree = entity.tree || [];
						entityNodes = [
							...entityNodes,
							...tree.filter(
								( entity ) => 'sfwd-quiz' === entity.type
							),
						];
					} );
				} );
			}
		} else {
			entityNodes = Object.values( present.questions );
		}

		const addedIDs = entityNodes.map( ( node ) => node.ID );
		return addedIDs;
	}

	/**
	 * Show text whether all or no lessons or quizzes have been added
	 *
	 * @param {string} type
	 * @param {boolean} hasLesson
	 * @param {boolean} hasQuiz
	 * @param {boolean} hasQuestion
	 */
	showAllOrNoElementAddedText( type, hasLesson, hasQuiz, hasQuestion ) {
		if ( 'lesson' === type ) {
			if ( 0 === hasLesson ) {
				return `<div className="editor-block-inspector__no-blocks"><p>${ LearnDashData.i18n.start_adding_element_labels[ type ] }.</p></div>`;
			}
			return `<div className="editor-block-inspector__no-blocks"><p>${ LearnDashData.i18n.all_elements_added_labels[ type ] }</p></div>`;
		}

		if ( 'quiz' === type ) {
			if ( 0 === hasQuiz ) {
				return `<div className="editor-block-inspector__no-blocks"><p>${ LearnDashData.i18n.start_adding_element_labels[ type ] }.</p></div>`;
			}
			return `<div className="editor-block-inspector__no-blocks"><p>${ LearnDashData.i18n.all_elements_added_labels[ type ] }</p></div>`;
		}

		if ( 'topic' === type ) {
			if (
				0 ===
				this.getRecentlyAddedTopicCount(
					this.props.workspace.present.lessons
				)
			) {
				return `<div className="editor-block-inspector__no-blocks"><p>${ LearnDashData.i18n.start_adding_element_labels[ type ] }.</p></div>`;
			}
			return `<div className="editor-block-inspector__no-blocks"><p>${ LearnDashData.i18n.all_elements_added_labels[ type ] }</p></div>`;
		}

		if ( 'question' === type ) {
			if ( 0 === hasQuestion ) {
				return `<div className="editor-block-inspector__no-blocks"><p>${ LearnDashData.i18n.start_adding_element_labels[ type ] }.</p></div>`;
			}
			return `<div className="editor-block-inspector__no-blocks"><p>${ LearnDashData.i18n.all_elements_added_labels[ type ] }</p></div>`;
		}
	}

	/**
	 * Maybe show the form.
	 */
	maybeShowForm() {
		const { latest_list, mode, all_list, list } = this.state;
		return (
			( 'latest' === mode && latest_list && 0 !== latest_list.length ) ||
			( list && 0 !== list.length ) ||
			( all_list && 0 !== all_list.length ) ||
			( 'search' === mode && list && 0 !== list.length ) ||
			( latest_list && 0 !== latest_list.length ) ||
			( 'all' === mode && all_list && 0 !== all_list.length )
		);
	}

	/**
	 * Render function
	 *
	 * @event addEntity
	 * @event toggleCheckboxChange
	 * @event handleInputChange
	 */
	render() {
		const {
			searchString,
			latest_list,
			mode,
			list,
			checkedItems,
			search_pagination,
			all_list_pagination,
		} = this.state;
		const { type, workspace } = this.props;
		const hasLesson = workspace.present.lessons.filter(
			( node ) => 'section-heading' !== node.type
		).length;
		const hasQuiz = workspace.present.quizzes.length;
		const hasQuestion = workspace.present.questions.length;

		// We can't add topics if there is no lesson
		if (
			0 === hasLesson &&
			'topic' === type &&
			'sfwd-quiz' !== LearnDashData.post_data.builder_post_type
		) {
			return (
				<div className="editor-block-inspector__no-blocks">
					<p>{ LearnDashData.labels[ 'start-adding-lesson' ] }</p>
				</div>
			);
		}
		return (
			<div className="ld__builder-sidebar-widget">
				{ /* Shows the search field, recent and all tabs */ }
				{ this.maybeShowForm() && (
					<form
						className="ld__builder-sidebar-form"
						onSubmit={ ( e ) => e.preventDefault() }
					>
						<input
							type="text"
							name="searchString"
							placeholder={ `${ LearnDashData.i18n.search_element_labels[ type ] }` }
							value={ searchString }
							onChange={ this.handleInputChange }
						/>

						<div className="ld__builder-sidebar-header">
							<a
								href="#"
								onClick={ ( e ) => {
									e.preventDefault();

									this.setState( {
										mode: 'latest',
										searchString: '',
									} );

									this.latestQuery();
								} }
								className={ classNames( {
									'ld__builder-sidebar-header-active':
										'latest' === mode,
								} ) }
							>
								{ `${ LearnDashData.i18n.recent }` }
							</a>

							<a
								href="#"
								onClick={ ( e ) => {
									e.preventDefault();

									this.setState( {
										mode: 'all',
										searchString: '',
									} );

									this.allQuery();
								} }
								className={ classNames( {
									'ld__builder-sidebar-header-active':
										'all' === mode,
								} ) }
							>
								{ `${ LearnDashData.i18n.view_all }` }
							</a>
						</div>
					</form>
				) }

				<Droppable
					droppableId={ JSON.stringify( {
						ID: `droppable-${ type }`,
						dnd: 'droppable',
					} ) }
					isDropDisabled={ true }
				>
					{ ( provided ) => (
						<ul
							ref={ provided.innerRef }
							className={ classNames( {
								'-recently-edited': 'latest' === mode,
							} ) }
						>
							{ list &&
								list.map(
									( contentItem, index ) =>
										this.canAddEntity( contentItem.ID ) && (
											<Draggable
												draggableId={ JSON.stringify( {
													...contentItem,
												} ) }
												index={ index }
												key={ contentItem.ID }
											>
												{ ( provided, snapshot ) => (
													<li
														key={ `${ type }-${ contentItem.ID }` }
														ref={
															provided.innerRef
														}
														{ ...provided.draggableProps }
														{ ...provided.dragHandleProps }
														className={ classNames(
															{
																'-is-dragging':
																	snapshot.isDragging,
															}
														) }
													>
														<div className="ld__builder-sidebar-item">
															<div>
																<input
																	id={ `${ type }-${ contentItem.ID }` }
																	type="checkbox"
																	className="components-checkbox-control__input"
																	onChange={ this.toggleCheckboxChange.bind(
																		this,
																		contentItem
																	) }
																	name="checkedItems"
																	value={
																		contentItem
																	}
																	checked={ checkedItems.includes(
																		contentItem
																	) }
																/>
																<label
																	htmlFor={ `${ this.props.type }-${ contentItem.ID }` }
																	dangerouslySetInnerHTML={ {
																		__html:
																			contentItem.post_title,
																	} }
																/>
															</div>
															<button
																onClick={ (
																	e
																) => {
																	this.addEntity.call(
																		this,
																		contentItem,
																		e
																	);

																	// Reset checkboxes
																	this.setState(
																		{
																			checkedItems: [],
																		}
																	);
																} }
																className="is-button is-primary components-button button button-primary"
															>
																{ `${ LearnDashData.i18n.add_element }` }
															</button>
														</div>
													</li>
												) }
											</Draggable>
										)
								) }
							{ 'search' === mode &&
								list &&
								0 === list.length && (
									<div>
										{ `${ LearnDashData.i18n.nothing_found }` }
									</div>
								) }

							{ ! this.maybeShowForm() && (
								<span
									dangerouslySetInnerHTML={ {
										__html: this.showAllOrNoElementAddedText(
											type,
											hasLesson,
											hasQuiz,
											hasQuestion
										),
									} }
								/>
							) }

							{ /* Show Refresh button for Recent tab if there are elements */ }
							{ 'latest' === mode &&
								latest_list &&
								0 !== latest_list.length && (
									<button
										href="#"
										className="ld__builder-sidebar-refresh"
										onClick={ ( e ) => {
											e.preventDefault();

											this.latestQuery();
										} }
									>
										{ `${ LearnDashData.i18n.refresh }` }
									</button>
								) }

							{ ( ( 'search' === mode &&
								search_pagination.has_next ) ||
								( 'all' === mode &&
									all_list_pagination.has_next ) ) && (
								<button
									href="#"
									className="ld__builder-sidebar-more"
									onClick={ ( e ) => {
										e.preventDefault();

										this.loadMore();
									} }
								>
									{ `${ LearnDashData.i18n.load_more }` }
								</button>
							) }

							{ provided.placeholder }
						</ul>
					) }
				</Droppable>

				{ /* Display the Add Selected button if at least two items are selected */ }
				{ 2 <= checkedItems.length && (
					<button
						disabled={ ! checkedItems.length }
						className="is-primary"
						onClick={ ( e ) => {
							// Add selected items
							checkedItems.map( ( content ) =>
								this.addEntity.call( this, content, e )
							);

							// Reset checkboxes
							this.setState( { checkedItems: [] } );
						} }
					>
						{ `${ LearnDashData.i18n.add_selected }` }
					</button>
				) }
			</div>
		);
	}
}

// Validate prop types
SidebarWidget.propTypes = {
	title: PropTypes.string,
	content: PropTypes.string,
	data: PropTypes.object,
	type: PropTypes.string,
	workspace: PropTypes.object,
	initData: PropTypes.func,
	addLessonEntity: PropTypes.func,
	addTopicEntity: PropTypes.func,
	addQuizEntity: PropTypes.func,
	addQuestionEntity: PropTypes.func,
	addFinalQuizEntity: PropTypes.func,
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = ( state ) => ( {
	...state,
} );

/**
 *
 * @param {*} dispatch
 */
const mapDispatchToProps = ( dispatch ) => ( {
	addLessonEntity: ( draggableLesson ) =>
		dispatch( addLessonEntity( draggableLesson ) ),
	addTopicEntity: ( draggableTopic ) =>
		dispatch( addTopicEntity( null, null, draggableTopic ) ),
	addQuizEntity: ( draggableQuiz ) =>
		dispatch( addQuizEntity( null, null, draggableQuiz ) ),
	addQuestionEntity: ( draggableQuestion ) =>
		dispatch( addQuestionEntity( draggableQuestion ) ),
	addFinalQuizEntity: ( index, draggableQuiz ) =>
		dispatch( addFinalQuizEntity( index, draggableQuiz ) ),
	initData: ( post_type ) => dispatch( initData( post_type ) ),
} );

export default connect( mapStateToProps, mapDispatchToProps )( SidebarWidget );
